import type { ProductKeys3P } from '@atlassian/rovo-connectors-config/types';

import type { CloudConfig } from '../../../../../common/constants/filters/cloud-config/types';
import type { CreatedSelectFilter } from '../../../../../common/constants/filters/select-filter/types';
import { ProductKeys } from '../../../../../common/constants/product-keys';
import type { SelectFilterOption } from '../../../../../common/ui/filters/select-filter';
import { getProducts3PFromConfigForFilter } from '../../../../../common/utils/filters';
import { createMultiSelectFilter } from '../../../../filters/multi-select-filter';
import { getCachedThirdPartyFilterOptions } from '../utils/third-party-filter-options-cache';

const salesforceProducts: ProductKeys[] = [ProductKeys.Salesforce];
const products3PFromConfig: ProductKeys3P[] = getProducts3PFromConfigForFilter('taskStatus');
const taskStatusProducts: ProductKeys[] = Array.from(
	new Set<ProductKeys>([...salesforceProducts, ...products3PFromConfig]),
);

const fetchDynamicTaskStatusOptions = async (
	config: CloudConfig,
): Promise<SelectFilterOption[]> => {
	const cloudId = config.cloudId;
	const datasourceId = config.selectedDatasource?.datasourceId;

	if (!cloudId || !datasourceId) {
		return [];
	}

	try {
		const result = await getCachedThirdPartyFilterOptions(config);

		if (!result) {
			return [];
		}

		const taskStatuses = result.filters.find((f) => f.name === 'taskStatuses');
		if (!taskStatuses || taskStatuses.values.length === 0) {
			return [];
		}

		return taskStatuses.values.map((taskStatus) => ({
			value: taskStatus.name,
			trackingKey: taskStatus.name,
			queryParamValue: taskStatus.name,
			isDefault: false,
			label: taskStatus.label ?? taskStatus.name,
			product: config.product,
			isSelected: false,
			isSuggested: false,
		}));
	} catch (e) {
		return [];
	}
};

const fetchTaskStatusOptions = async (config: CloudConfig): Promise<SelectFilterOption[]> => {
	return fetchDynamicTaskStatusOptions(config);
};

const fetchQueryParams = async (
	queryParams: string[],
	config: CloudConfig,
): Promise<SelectFilterOption[]> => {
	const allOptions = await fetchTaskStatusOptions(config);
	const selected = new Set(queryParams);
	const result = allOptions.filter((option) => selected.has(option.queryParamValue));
	return result;
};

export const filter: CreatedSelectFilter<'taskStatus'> = createMultiSelectFilter({
	id: 'taskStatus',
	requiresTopLevel3PDatasource: true,
	products: taskStatusProducts,
	alwaysVisible: false,
	universal: false,
	queryParams: {
		key: 'taskStatuses',
		fetcher: async (queryParams, config) => {
			return fetchQueryParams(queryParams, config);
		},
	},
	initialOptions: {
		fetcher: async (config) => {
			return fetchTaskStatusOptions(config);
		},
	},
});
